import React, { useRef } from 'react';
import { styled } from '@linaria/react';
import { COLORS, Icon, typographyStyles } from '@livecommerce/design-system';
import { useHistory } from 'react-router-dom';

import OrganizationSwitcher from 'components-v3/OrganizationSwitcher/index.jsx';
import { ONE_TO_MANY_PRODUCT, ONE_TO_ONE_PRODUCT } from 'constants/products.js';
import * as ROUTES from 'constants/routes.js';
import { trackingService } from 'services/tracking/TrackingService.js';
import { useOrganizations, useProductDataOutsideRoutes } from 'hooks/index.js';

export const { getBodyText } = typographyStyles;

const MenuList = styled.ul`
  display: flex;
  flex-direction: column;
  list-style-type: none;
  width: 100%;
  padding: 0;
  margin: 0;
`;

const MenuItem = styled.li`
  width: 100%;
`;

const MenuTitle = styled.p`
  align-self: center;
  grid-column: 2 / 3;
  grid-row: 1 / 3;
  margin: 0;
`;

const ProductIcon = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 6px;
  background-color: ${COLORS.blackLight};
  color: ${COLORS.light_200};
  ${getBodyText(16)};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const MenuProductIcon = styled(ProductIcon)`
  grid-column: 1 / 2;
  grid-row: 1 / 3;
`;

const StyledCheckmark = styled(Icon)`
  align-self: center;
  grid-column: 3 / 4;
  grid-row: 1 / 3;
  background-color: ${COLORS.light_200};
  border-radius: 8px;
  color: ${COLORS.dark_700};
`;

const MenuItemButton = styled.button`
  width: 100%;
  text-align: left;
  background-color: transparent;
  border: 0;
  border-radius: 6px;
  ${getBodyText(14)};
  padding: 8px 12px 8px 8px;
  margin: 0;
  white-space: nowrap;
  cursor: pointer;

  display: grid;
  grid-template-rows: 1fr 1fr;
  grid-template-columns: 44px 1fr 24px;
  column-gap: 12px;
  min-width: 240px;

  &:hover {
    background-color: ${COLORS.dark_700};
    color: ${COLORS.white};

    ${StyledCheckmark} {
      background-color: ${COLORS.dark_500};
      color: ${COLORS.light_200};
    }
  }
`;
const SwitchToText = styled.p`
  ${getBodyText(14)};
  color: ${COLORS.dark_700};
  margin: 12px 0 8px;
`;

const organizationsMenuItems = [
  {
    icon: '1:1',
    id: ONE_TO_ONE_PRODUCT,
    path: '',
  },
  {
    icon: '1:M',
    id: ONE_TO_MANY_PRODUCT,
    path: '',
  },
];

const Container = styled.div`
  padding: 12px;
`;

export const TITLES = {
  [ONE_TO_ONE_PRODUCT]: 'Video Consultation',
  [ONE_TO_MANY_PRODUCT]: 'Live',
};

const ProductsMenu = ({
  isOrganizationSwitcherOpened,
  setIsOrganizationSwitcherOpened,
  setIsProductsMenuOpened,
  ...props
}) => {
  const { data: organizations } = useOrganizations();
  const history = useHistory();
  const switcherRef = useRef(null);
  const { selectedProduct, orgId } = useProductDataOutsideRoutes();

  const handleClick = (e) => {
    if (switcherRef.current && switcherRef.current.contains(e.target)) return;
    if (!isOrganizationSwitcherOpened) return;

    setIsOrganizationSwitcherOpened(false);
  };

  const handleChangeProductClick = (product) => {
    setIsProductsMenuOpened(false);

    if (product === selectedProduct) return;

    trackingService.track({
      eventType: `product-switcher:click:${product}`,
    });

    const routeOrgId = orgId || organizations[0]?.id;

    if (product === ONE_TO_ONE_PRODUCT) {
      history.push(ROUTES.buildRoute(ROUTES.ORG_ONE_TO_ONE_HOME, { orgId: routeOrgId }));
      return;
    }
    if (product === ONE_TO_MANY_PRODUCT) {
      history.push(ROUTES.buildRoute(ROUTES.ORG_ONE_TO_MANY_HOME, { orgId: routeOrgId }));
    }
  };

  const handleOrganizationSwitcherOpen = (opened) => {
    const nextOpened = !isOrganizationSwitcherOpened;
    trackingService.track({
      eventType: `org-switcher:${nextOpened ? 'open' : 'close'}`,
    });
    setIsOrganizationSwitcherOpened(opened);
  };

  return (
    <Container onClick={handleClick}>
      <OrganizationSwitcher
        dropdownContentRef={switcherRef}
        isOrganizationSwitcherOpened={isOrganizationSwitcherOpened}
        setIsOrganizationSwitcherOpened={handleOrganizationSwitcherOpen}
        setIsProductsMenuOpened={setIsProductsMenuOpened}
      />
      <SwitchToText>Switch to</SwitchToText>
      <MenuList {...props}>
        {organizationsMenuItems.map(({ id, path, icon }) => (
          <MenuItem key={id} aria-label={id} aria-current={selectedProduct === id}>
            <MenuItemButton onClick={() => handleChangeProductClick(id)}>
              <MenuProductIcon>
                <Icon name={icon} color="white" />
              </MenuProductIcon>
              <MenuTitle>{TITLES[id]}</MenuTitle>
              {selectedProduct === id && <StyledCheckmark name="checkmark" />}
            </MenuItemButton>
          </MenuItem>
        ))}
      </MenuList>
    </Container>
  );
};

export default ProductsMenu;
